import { PersonaConditionLeaf } from '@/common/api/params/persona'

/**
 * 判定条件の閾値を編集するための共通ロジック。
 *
 * コンポーネント（`ConditionLeafEditor.tsx`）とは分けて置く。同じファイルから
 * コンポーネント以外を export すると Fast Refresh が効かなくなるため。
 */

/** 画面で扱える演算子。in / exists は式が化けるため編集させない */
export const EDITABLE_OPS = ['>=', '>', '<=', '<', '=', '!='] as const

export const OP_LABELS: Record<string, string> = {
  '>=': '以上',
  '>': 'より大きい',
  '<=': '以下',
  '<': '未満',
  '=': 'と等しい',
  '!=': 'と等しくない'
}

/**
 * 条件式を編集できる形（フラットな all のリーフ列）に開く。
 *
 * 入れ子や in / exists を含むものは画面で正しく表せないため null を返し、
 * 呼び出し側は読み取り専用にする。中途半端に編集させて別の式に化ける方が危ない。
 */
export const toEditableLeaves = (
  conditions: Record<string, unknown> | null
): PersonaConditionLeaf[] | null => {
  if (!conditions || Object.keys(conditions).length !== 1) return null
  const all = (conditions as { all?: unknown }).all
  if (!Array.isArray(all) || all.length === 0) return null
  const leaves: PersonaConditionLeaf[] = []
  for (const node of all) {
    if (typeof node !== 'object' || node === null) return null
    const leaf = node as Record<string, unknown>
    if ('all' in leaf || 'any' in leaf || 'not' in leaf) return null
    const { metric, op, src } = leaf
    if (typeof metric !== 'string' || typeof op !== 'string') return null
    if (!EDITABLE_OPS.includes(op as (typeof EDITABLE_OPS)[number])) return null
    if (src !== undefined && src !== 'raw' && src !== 'pct') return null
    // src を落とすと「サイト内の上位10％」が「10円以上」に化ける。必ず持ち回る
    leaves.push({ metric, op, value: leaf.value, ...(src ? { src } : {}) })
  }
  return leaves
}

/** 値が未入力のリーフがあるか（保存を止めるために使う） */
export const hasEmptyValue = (leaves: PersonaConditionLeaf[]) =>
  leaves.some((l) => l.value === '' || l.value === undefined)
